import React, { useCallback } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View } from 'react-native';
import { Platform, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import type { AddressType } from '@sweetgreen/garnish';
import { useResponsive } from '@sweetgreen/garnish';
import { useBreakpointHelpers } from '@sweetgreen/garnish';

import { useCart } from '@order/hooks';

import { useBagRestaurantIds } from '../../../components/Bag/Bag.hooks';
import type { UseNavigateToMenuProps } from '../../../hooks/useNavigateToMenu';
import { useNavigateToMenu } from '../../../hooks/useNavigateToMenu';
import type {
  LocationsScreenProps,
  MenuTabStackParamList,
} from '../../../navigation/AppNavigation.props';
import { useHomeNavigation } from '../../HomeScreen/hooks';
import {
  useCancelReorder,
  useChangeLocation,
  useConfirmLocation,
  useConflictReview,
  useReorderState,
} from '../../ReorderingScreen/hooks';
import { LocationSearchTypePicker } from '../components/SearchBar/subcomponents';

// Navigate to restaurant and register last visited store.
export const useNavigateToMenuFromLocation = () => {
  const navigateToMenu = useNavigateToMenu();
  const { currentStep } = useReorderState();
  const conflictReview = useConflictReview();
  const { cart } = useCart();
  const { bagRestaurantSlug, bagAddressId } = useBagRestaurantIds();
  const isReordering = currentStep !== 'idle';

  return React.useCallback(
    (props: UseNavigateToMenuProps) => {
      // if you are already reordering then go to the next step in the flow.
      if (isReordering) {
        void conflictReview({
          orderType: props.orderType,
          deliveryAddressName: props.deliveryAddressName,
          deliveryOrderDetail: props.deliveryOrderDetail,
          restaurantId: props.restaurantId,
          restaurantSlug: props.restaurantSlug,
          restaurantName: props.restaurantName,
        });

        return;
      }

      const isSameAddressId =
        bagAddressId === props.deliveryOrderDetail?.addressId;
      const hasBag = Boolean(bagRestaurantSlug) || Boolean(bagAddressId);
      const needsToReorder = hasBag && !isSameAddressId;

      // if your bag belongs to a different restaurant, start reorder flow
      if (needsToReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: props.orderType,
          deliveryAddressName: props.deliveryAddressName,
          deliveryOrderDetail: props.deliveryOrderDetail,
          restaurantId: props.restaurantId,
          restaurantSlug: props.restaurantSlug,
          restaurantName: props.restaurantName,
          finishOnMenu: true,
        });

        return;
      }

      navigateToMenu(props);
    },
    [
      cart?.id,
      isReordering,
      bagAddressId,
      bagRestaurantSlug,
      navigateToMenu,
      conflictReview,
    ],
  );
};

export const useNavigationActions = () => {
  const { handleGoHome } = useHomeNavigation();
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;

  // ──────── Reordering ────────
  const { currentStep, previousStep } = useReorderState();
  const changeLocation = useChangeLocation();
  const confirmLocation = useConfirmLocation();
  const conflictReview = useConflictReview();
  const cancelReorder = useCancelReorder();
  const isReordering = currentStep !== 'idle';
  const canGoBack = isReordering && Boolean(previousStep);
  const canClose = isReordering || isMobile;
  const onBack = React.useCallback(() => {
    if (!canGoBack) return;

    if (previousStep === 'locationSearch') changeLocation();

    if (previousStep === 'locationConfirmation') confirmLocation();

    if (previousStep === 'conflictReview') void conflictReview();
  }, [
    canGoBack,
    previousStep,
    changeLocation,
    confirmLocation,
    conflictReview,
  ]);

  // ──────── Callbacks ────────
  const onClose = React.useCallback(() => {
    if (isReordering) {
      cancelReorder();

      return;
    }

    handleGoHome();
  }, [isReordering, handleGoHome, cancelReorder]);

  return {
    onBack: canGoBack ? onBack : undefined,
    onClose: canClose ? onClose : undefined,
  };
};

// Returns locationSearchType (pickup, delivery, outpost) from navigation params.
export const useLocationSearchType = () => {
  const { params } = useRoute<RouteProp<MenuTabStackParamList, 'Locations'>>();
  const route = params?.locationSelectionType ?? 'pickup';
  const [locationSearchType, setLocationSearchType] =
    React.useState<AddressType>(route);

  return { locationSearchType, setLocationSearchType };
};

// For mobile header.
export const useLocationSearchTypePicker = (
  props: UseLocationSearchTypePickerProps,
) => {
  const {
    navigation,
    locationSearchType,
    isDisabled = false,
    setLocationSearchType,
    setSelectedLocation,
  } = props;

  const { currentBreakpoint } = useResponsive();

  // ─── Animated Values ─────────────────────────────────────────────────

  const headerContainerOpacity = useSharedValue(0);
  const headerContainerWidth = useSharedValue(0);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleSelect = React.useCallback(
    (searchType: AddressType) => {
      navigation.setParams({ locationSelectionType: searchType });
      setLocationSearchType(searchType);
      setSelectedLocation('');
    },
    [navigation, setLocationSearchType, setSelectedLocation],
  );

  const setHeaderAnimatedStylesValues = useCallback(
    (event: LayoutChangeEvent) => {
      // eslint-disable-next-line functional/immutable-data
      headerContainerWidth.value = event.nativeEvent.layout.width;

      // eslint-disable-next-line functional/immutable-data
      headerContainerOpacity.value = 1;
    },
    [headerContainerOpacity, headerContainerWidth],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  // This hook returns a custom `headerTitle` component that renders `LocationSearchTypePicker`.
  //
  // The header title container in React Navigation has a `maxWidth` property to prevent title overflow.
  // Unfortunately, it causes layout issues in our situation because the content of this component is larger
  // than the specified `maxWidth`. Fortunately the issue only occurs on the Web platform.
  //
  // There is a corresponding `headerTitleContainerStyle` property, which in theory can be used to reset
  // the previously mentioned `maxWidth`, but as of this writing (10/22) it is not working (prop is not passed).
  //
  // That is why we employ the tried-and-true absolute position + transform approach to
  // bypass `maxWidth` and correctly place our custom header.

  const webAnimatedStyle = useAnimatedStyle(() => {
    return {
      position: 'absolute',
      left: '-50%',
      transform: [{ translateX: -(headerContainerWidth.value / 2) }],
      opacity: withTiming(headerContainerOpacity.value), // to hide component jumping on transform, we animate opacity
    };
  });
  const containerStyle = currentBreakpoint.isMobile
    ? Platform.select({ web: webAnimatedStyle })
    : styles.locationsSearchTypeHeader;
  const Wrapper = currentBreakpoint.isMobile ? Animated.View : View;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Wrapper onLayout={setHeaderAnimatedStylesValues} style={containerStyle}>
      <LocationSearchTypePicker
        searchType={locationSearchType}
        onSelect={handleSelect}
        isDisabled={isDisabled}
      />
    </Wrapper>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseLocationSearchTypePickerProps = {
  readonly locationSearchType: AddressType;
  readonly setLocationSearchType: (
    value: React.SetStateAction<AddressType>,
  ) => void;
  readonly setSelectedLocation: (value: React.SetStateAction<string>) => void;
  readonly isDisabled?: boolean;
} & Pick<LocationsScreenProps, 'navigation'>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  locationsSearchTypeHeader: {
    flex: 1,
    alignItems: 'center',
  },
});
